/**
 * Documentation of Emergency Plan Delivery Tab Component
 * Form for documenting emergency plan delivery to local and county municipalities
 */
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { FileText, Printer, Save, History, Download, Loader2, Upload, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUBMISSION_METHODS = [
  { value: "mail", label: "Mail" },
  { value: "fax", label: "Fax" },
  { value: "email", label: "Email" },
  { value: "hand_delivered", label: "Hand Delivered" }
];

export const EmergencyPlanDeliveryTab = ({ onComplianceChange }) => {
  const { organization } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [savedRecords, setSavedRecords] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [itemId, setItemId] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Facility Information
    facilityName: "",
    facilityAddress: "",
    legalEntity: "",
    
    // Local Municipality
    localMunicipalityName: "",
    localMunicipalityAddress: "",
    localMunicipalityPhone: "",
    localMunicipalityEmail: "",
    localMunicipalityFax: "",
    localSubmissionMethod: "",
    localSubmissionDate: "",
    
    // County Municipality
    countyMunicipalityName: "",
    countyMunicipalityAddress: "",
    countyMunicipalityPhone: "",
    countyMunicipalityEmail: "",
    countyMunicipalityFax: "",
    countySubmissionMethod: "",
    countySubmissionDate: "",
    
    // Self-Certification
    certifierName: "",
    certifierTitle: "",
    certificationDate: format(new Date(), 'yyyy-MM-dd')
  });

  useEffect(() => {
    fetchRecords();
    fetchItemId();
  }, []);

  const fetchItemId = async () => {
    try {
      const res = await axios.get(`${API}/compliance/status`);
      const item = (res.data?.items || []).find(i =>
        i.name?.toLowerCase().includes('emergency plan delivery') ||
        i.name?.toLowerCase().includes('documentation of emergency plan')
      );
      if (item?.id) {
        setItemId(item.id);
        fetchUploadedDocs(item.id);
      }
    } catch {}
  };

  const fetchUploadedDocs = async (id) => {
    try {
      const res = await axios.get(`${API}/compliance/documents/${id}`);
      setUploadedDocs(res.data?.documents || []);
    } catch {}
  };

  const handleFileUpload = async (file) => {
    if (!file || !itemId) return;
    const allowed = ['application/pdf','image/jpeg','image/png','image/jpg'];
    if (!allowed.includes(file.type)) { toast.error("Please upload a PDF or image file"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be under 15MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/compliance/documents/${itemId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success("Document uploaded — compliance updated");
      fetchUploadedDocs(itemId);
      if (onComplianceChange) onComplianceChange();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  useEffect(() => {
    if (organization?.name) {
      setFormData(prev => ({
        ...prev,
        facilityName: prev.facilityName || organization.name,
        facilityAddress: prev.facilityAddress || organization.address || ""
      }));
    }
  }, [organization]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/compliance/emergency-plan-delivery`);
      setSavedRecords(res.data?.records || []);
      
      // Load the most recent record if exists
      if (res.data?.records?.length > 0) {
        const latest = res.data.records[0];
        setFormData(prev => ({
          ...prev,
          ...latest,
          certificationDate: latest.certificationDate || format(new Date(), 'yyyy-MM-dd')
        }));
      }
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.facilityName) {
      toast.error("Facility name is required");
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(`${API}/compliance/emergency-plan-delivery`, formData);
      toast.success("Emergency Plan Delivery documentation saved");
      fetchRecords();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save documentation");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Documentation of Emergency Plan Delivery</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .logo-header { margin-bottom: 8px; }
          .logo-header img { height: 50px; width: auto; }
          .center-name { text-align: center; font-size: 22px; font-weight: bold; color: #1F2937; margin-bottom: 15px; }
          h1 { text-align: center; font-size: 18px; margin-bottom: 20px; }
          .intro { font-size: 12px; margin-bottom: 20px; font-style: italic; }
          .section { margin-bottom: 25px; }
          .section-title { font-weight: bold; font-size: 14px; margin-bottom: 10px; border-bottom: 1px solid #000; padding-bottom: 5px; }
          .field { margin-bottom: 8px; }
          .field-label { font-weight: bold; font-size: 12px; }
          .field-value { font-size: 12px; border-bottom: 1px solid #ccc; min-height: 18px; padding: 2px 0; }
          .signature-section { margin-top: 40px; }
          .signature-line { border-bottom: 1px solid #000; width: 250px; display: inline-block; margin-right: 20px; }
          .footer { margin-top: 40px; font-size: 10px; text-align: right; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="logo-header"><img src="/simple-sign-in-logo.png" alt="Simple Sign In" onerror="this.style.display='none'" /></div>
        <div class="center-name">${formData.facilityName || organization?.name || 'Childcare Center'}</div>
        <h1>DOCUMENTATION OF EMERGENCY PLAN DELIVERY</h1>
        <p class="intro">Facilities must retain documentation that they submitted their initial emergency plan and any updated plan to their local and county municipalities.</p>
        
        <div class="section">
          <div class="section-title">Facility Information</div>
          <div class="field">
            <span class="field-label">Facility Location/Name:</span>
            <div class="field-value">${formData.facilityName || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Location Physical Address:</span>
            <div class="field-value">${formData.facilityAddress || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Legal Entity/Person responsible:</span>
            <div class="field-value">${formData.legalEntity || ''}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Local Municipality (i.e. borough, township, city, district)</div>
          <div class="field">
            <span class="field-label">Name:</span>
            <div class="field-value">${formData.localMunicipalityName || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Address:</span>
            <div class="field-value">${formData.localMunicipalityAddress || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Phone Number:</span>
            <div class="field-value">${formData.localMunicipalityPhone || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Email Address:</span>
            <div class="field-value">${formData.localMunicipalityEmail || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Fax Number:</span>
            <div class="field-value">${formData.localMunicipalityFax || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Method Submitted:</span>
            <div class="field-value">${formData.localSubmissionMethod || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Date Submitted:</span>
            <div class="field-value">${formData.localSubmissionDate || ''}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">County Municipality</div>
          <div class="field">
            <span class="field-label">Name:</span>
            <div class="field-value">${formData.countyMunicipalityName || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Address:</span>
            <div class="field-value">${formData.countyMunicipalityAddress || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Phone Number:</span>
            <div class="field-value">${formData.countyMunicipalityPhone || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Email Address:</span>
            <div class="field-value">${formData.countyMunicipalityEmail || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Fax Number:</span>
            <div class="field-value">${formData.countyMunicipalityFax || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Method Submitted:</span>
            <div class="field-value">${formData.countySubmissionMethod || ''}</div>
          </div>
          <div class="field">
            <span class="field-label">Date Submitted:</span>
            <div class="field-value">${formData.countySubmissionDate || ''}</div>
          </div>
        </div>
        
        <div class="section signature-section">
          <div class="section-title">Self-Certification</div>
          <p style="font-size: 12px; margin-bottom: 20px;">I hereby swear/affirm that the information provided is true and correct to the best of my knowledge.</p>
          <div style="margin-top: 30px;">
            <span class="signature-line"></span>
            <span class="signature-line" style="width: 150px;"></span>
            <span class="signature-line" style="width: 100px;"></span>
          </div>
          <div style="font-size: 10px; margin-top: 5px;">
            <span style="margin-right: 170px;">Signature</span>
            <span style="margin-right: 100px;">Title</span>
            <span>Date</span>
          </div>
        </div>
        
        <div class="footer">CD 355 11/20</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadTemplate = () => {
    // Download the original PDF template
    window.open('https://customer-assets.emergentagent.com/job_ea8e734a-6f75-47f4-b660-4f3e3d009cac/artifacts/khlkvmjv_DOCUMENTATION%20OF%20EMERGENCY%20PLAN%20DELIVERY.pdf', '_blank');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg text-blue-800">
            <FileText className="w-5 h-5 text-blue-600" />
            Documentation of Emergency Plan Delivery
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download className="w-4 h-4 mr-1" />
              Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)}>
              <History className="w-4 h-4 mr-1" />
              History ({savedRecords.length})
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Document emergency plan delivery to local and county municipalities
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {showHistory && savedRecords.length > 0 && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Saved Records</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {savedRecords.map((record, idx) => (
                <div 
                  key={idx} 
                  className="text-sm p-2 bg-white rounded border cursor-pointer hover:bg-blue-50"
                  onClick={() => setFormData(prev => ({ ...prev, ...record }))}
                >
                  <span className="font-medium">{record.facilityName}</span>
                  <span className="text-gray-500 ml-2">
                    - Certified: {record.certificationDate || 'N/A'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-sm text-gray-600 italic border-l-4 border-blue-200 pl-3">
          Facilities must retain documentation that they submitted their initial emergency plan and any updated plan to their local and county municipalities.
        </p>

        {/* Preloaded Template Notice — informational only */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="preloaded-template-notice">
          <FileText className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            A preloaded Documentation of Emergency Plan Delivery template is available below for your convenience. You may use the provided template or upload your own completed document.
          </p>
        </div>

        {/* Facility Information */}
        <div className="space-y-4 p-4 rounded-lg border-2 border-blue-200 bg-blue-50/20">
          <h3 className="font-semibold text-md border-b-2 border-blue-300 pb-2 text-blue-800">Facility Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Facility Location/Name</Label>
              <Input
                value={formData.facilityName}
                onChange={(e) => handleInputChange('facilityName', e.target.value)}
                placeholder="Enter facility name"
              />
            </div>
            <div className="space-y-2">
              <Label>Legal Entity/Person Responsible</Label>
              <Input
                value={formData.legalEntity}
                onChange={(e) => handleInputChange('legalEntity', e.target.value)}
                placeholder="Enter legal entity or responsible person"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Location Physical Address</Label>
            <Textarea
              value={formData.facilityAddress}
              onChange={(e) => handleInputChange('facilityAddress', e.target.value)}
              placeholder="Enter physical address"
              rows={2}
            />
          </div>
        </div>

        {/* Local Municipality */}
        <div className="space-y-4 p-4 rounded-lg border-2 border-blue-200 bg-blue-50/20">
          <h3 className="font-semibold text-md border-b-2 border-blue-300 pb-2 text-blue-800">Local Municipality (borough, township, city, district)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.localMunicipalityName}
                onChange={(e) => handleInputChange('localMunicipalityName', e.target.value)}
                placeholder="Municipality name"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                value={formData.localMunicipalityPhone}
                onChange={(e) => handleInputChange('localMunicipalityPhone', e.target.value)}
                placeholder="Phone number"
              />
            </div>
            <div className="space-y-2">
              <Label>Email Address (if available)</Label>
              <Input
                value={formData.localMunicipalityEmail}
                onChange={(e) => handleInputChange('localMunicipalityEmail', e.target.value)}
                placeholder="Email address"
              />
            </div>
            <div className="space-y-2">
              <Label>Fax Number (if available)</Label>
              <Input
                value={formData.localMunicipalityFax}
                onChange={(e) => handleInputChange('localMunicipalityFax', e.target.value)}
                placeholder="Fax number"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Textarea
              value={formData.localMunicipalityAddress}
              onChange={(e) => handleInputChange('localMunicipalityAddress', e.target.value)}
              placeholder="Municipality address"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Method Submitted</Label>
              <select
                className="w-full border rounded-md p-2"
                value={formData.localSubmissionMethod}
                onChange={(e) => handleInputChange('localSubmissionMethod', e.target.value)}
              >
                <option value="">Select method...</option>
                {SUBMISSION_METHODS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Date Submitted</Label>
              <Input
                type="date"
                value={formData.localSubmissionDate}
                onChange={(e) => handleInputChange('localSubmissionDate', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* County Municipality */}
        <div className="space-y-4 p-4 rounded-lg border-2 border-blue-200 bg-blue-50/20">
          <h3 className="font-semibold text-md border-b-2 border-blue-300 pb-2 text-blue-800">County Municipality</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.countyMunicipalityName}
                onChange={(e) => handleInputChange('countyMunicipalityName', e.target.value)}
                placeholder="County municipality name"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                value={formData.countyMunicipalityPhone}
                onChange={(e) => handleInputChange('countyMunicipalityPhone', e.target.value)}
                placeholder="Phone number"
              />
            </div>
            <div className="space-y-2">
              <Label>Email Address (if available)</Label>
              <Input
                value={formData.countyMunicipalityEmail}
                onChange={(e) => handleInputChange('countyMunicipalityEmail', e.target.value)}
                placeholder="Email address"
              />
            </div>
            <div className="space-y-2">
              <Label>Fax Number (if available)</Label>
              <Input
                value={formData.countyMunicipalityFax}
                onChange={(e) => handleInputChange('countyMunicipalityFax', e.target.value)}
                placeholder="Fax number"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Textarea
              value={formData.countyMunicipalityAddress}
              onChange={(e) => handleInputChange('countyMunicipalityAddress', e.target.value)}
              placeholder="County municipality address"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Method Submitted</Label>
              <select
                className="w-full border rounded-md p-2"
                value={formData.countySubmissionMethod}
                onChange={(e) => handleInputChange('countySubmissionMethod', e.target.value)}
              >
                <option value="">Select method...</option>
                {SUBMISSION_METHODS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Date Submitted</Label>
              <Input
                type="date"
                value={formData.countySubmissionDate}
                onChange={(e) => handleInputChange('countySubmissionDate', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Self-Certification */}
        <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-md border-b pb-2">Self-Certification</h3>
          <p className="text-sm text-gray-600 italic">
            I hereby swear/affirm that the information provided is true and correct to the best of my knowledge.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.certifierName}
                onChange={(e) => handleInputChange('certifierName', e.target.value)}
                placeholder="Your name"
              />
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.certifierTitle}
                onChange={(e) => handleInputChange('certifierTitle', e.target.value)}
                placeholder="Your title"
              />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.certificationDate}
                onChange={(e) => handleInputChange('certificationDate', e.target.value)}
              />
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-400 text-right">CD 355 11/20</p>
      </CardContent>
    </Card>
  );
};

export default EmergencyPlanDeliveryTab;
